
















































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { Entities, FeedTank, FeedTankLegShapes, FeedTankLegShapesEnum, FeedTankType, FeedTypeLevelMetricEnum } from '@/api';
import { numberInputValid } from '@/common/Validators';
import { FeedProductModals, FeedType } from '../../../../models';
import Toast from '@/components/display/toast.vue';
import { FeedProductService } from '../../../../services';
import { FormSelectOption } from '@/models/form-select-option';
import SaveCancelButtons from '@/components/elements/buttons/save-cancel-buttons.vue';
import LegShapeGuideModal from '@/pages/account/products/feed/components/LegShapeGuideModal.vue';
import CustomFormulaConfiguration from '@/components/form pickers/CustomFormulaConfiguration.vue';
import { FormulaInput } from '@/modules/formulas/models/formula-input';
import { FormulasService } from '@/modules/formulas/formulas-service';
import { FuelTankVolumeCalculator } from '@/typings/fuel-volume-calculator';
import {
	SensorType,
	SENSOR_OPTIONS,
	isRadar,
	isStrain,
} from '@/pages/account/products/feed/models/sensor-config';

@Component({
	components: { CustomFormulaConfiguration, SaveCancelButtons, LegShapeGuideModal },
})
export default class ProvisionFeedTankModal extends Vue {
	@Prop({ required: true, type: Object })
	public feedTank!: FeedTank & { feedtype: FeedType; sensorType?: SensorType };

	@Prop({ required: false, type: Boolean, default: false })
	public provisionedPreviously!: boolean;

	@Prop({ required: true, type: Function })
	private onProvision!: (feedTank: FeedTank) => Promise<void>;

	public sensorType: SensorType = this.feedTank.sensorType ?? "radar";

	public readonly sensorOptions = SENSOR_OPTIONS;

	public get isRadarSelected(): boolean {
		return isRadar(this.sensorType);
	}

	public get isStrainSelected(): boolean {
		return isStrain(this.sensorType);
	}



	@Watch('feedTank')
	public onFeedTankChanged(): void {
		this.resetValues();
	}

	@Watch('sensorType')
	public onSensorTypeChanged(newSensorType: SensorType): void {
		// Reset feedTypeId if current selection is not valid for new sensor type
		const validFeedTypeIds = this.filteredFeedTypes.map(ft => ft.value);
		if (!validFeedTypeIds.includes(this.feedTypeId)) {
			this.feedTypeId = (validFeedTypeIds[0] as number) ?? null;
		} else {
			this.feedTypeId = this.feedTank.feedTypeId ?? null;
		}
	}

	public get nameValid(): boolean {
		return this.name !== null && this.name.trim().length > 0;
	}

	public nominalValid(): boolean | null {
		if (this.nominalVolume === null) return false;
		return (Number.isInteger(this.nominalVolume) && this.nominalVolume >= 0);
	}

	public nominalWeightValid(): boolean {
		if (this.nominalWeight === null) return false;
		return parseFloat(this.nominalWeight.toFixed(1)) === this.nominalWeight;
	}

	public get numTanksValid(): boolean | null {

		return numberInputValid({
			field: this.numTanks,
			min: 1,
			max: 10,
		});
	}

	public get numLegsValid(): boolean | null {
		if (!Number.isFinite(this.numLegs as any)) return null;
		return Number.isInteger(this.numLegs) && (this.numLegs as number) >= 3 && (this.numLegs as number) <= 99;
	}

	public get feedTankTypes(): FormSelectOption[] {
		return [
			{ value: FeedTankType.Silo, text: 'Silo' },
			{ value: FeedTankType.Tank, text: 'Tank' },
			{ value: FeedTankType.Vat, text: 'Vat' },
			{ value: FeedTankType.Bin, text: 'Bin' },
			{ value: FeedTankType.Null, text: 'Unknown' },
		];
	}

	public get filteredFeedTypes(): FormSelectOption[] {
		if (!this.rawFeedTypes) return [];

		// Strain: filter to Weight only; Radar: show all
		const feedTypes = this.isRadarSelected
			? this.rawFeedTypes
			: this.rawFeedTypes.filter(ft => ft.levelMetric === FeedTypeLevelMetricEnum.Weight);

		return feedTypes.map(ft => ({ value: ft.id, text: ft.name }));
	}

	public get feedTypeSelectOptions(): FormSelectOption[] {
		return [{ value: null, text: 'Select a feed type' }, ...this.filteredFeedTypes];
	}

	public get feedTypeValid(): boolean {
		return this.feedTypeId != null;
	}

	private get formulaOptions() {
		return this.formulasService.formulaOptions;
	}

	public name: string | null = this.feedTank.name ?? null;
	public nominalVolume: number | null = this.feedTank.nominalVolume ?? null;
	// Convert kg from API to tons for display (1 ton = 1000 kg)
	public nominalWeight: number | null = this.feedTank.nominalWeight ? this.feedTank.nominalWeight / 1000 : null;
	public numTanks: number = this.feedTank.numTanks ?? 1;
	public feedTypeId: number | null = this.feedTank.feedTypeId ?? null;
	public feedTankType: FeedTankType = this.feedTank.type ?? FeedTankType.Silo;
	public numLegs: number | null = this.feedTank.numLegs ?? null;
	public legShape: FeedTankLegShapesEnum | null = this.feedTank.legShape ?? null;
	public brand: string | null = this.feedTank.brand ?? null;
	public legShapeOptions: { value: FeedTankLegShapesEnum | null; text: string }[] = [
		{ value: null, text: 'Select leg shape (optional)' },
	];
	public modalId: string = FeedProductModals.PROVISION;
	public loading: boolean = true;
	public submitted: boolean = false;
	public feedTypes: FormSelectOption[] | null = null;
	private rawFeedTypes: FeedType[] | undefined;

	private formulaInputs: FormulaInput[] = [];
	private calculatingTank = false;
	private calculation: any = {};
	private parameters: Record<string, any> = {};

	private readonly formulasService = new FormulasService(Entities.FeedTanks);
	private readonly toast: Toast = new Toast();

	public async created(): Promise<void> {
		this.rawFeedTypes = await this.getFeedTypes();
		if (this.rawFeedTypes) {
			this.feedTypes = this.rawFeedTypes.map((feedType: FeedType) => ({
				value: feedType.id,
				text: feedType.name,
			}));
		}

		const legShapesResponse = await new FeedProductService().getFeedTankLegShapes();
		if (legShapesResponse.legShapes) {
			this.legShapeOptions = [
				{ value: null, text: 'Select leg shape (optional)' },
				...legShapesResponse.legShapes.map((s: FeedTankLegShapes) => ({ value: s.value as FeedTankLegShapesEnum, text: s.label })),
			];
		}

		await this.getFormulas();

		this.loading = false;
	}

	@Watch('feedTank')
	private async getFormulas() {
		await this.formulasService.initalise();
		if (this.feedTank.formulaId) {
			this.formulaInputs = this.formulasService.getFormulaInputs(this.feedTank.formulaId) ?? [];
			if (this.feedTank.formulaParameters) {
				const parameters: Record<string, any> = this.feedTank.formulaParameters;
				this.formulaInputs.forEach((input: FormulaInput) => {
					input.value = parameters[input.name];
				});
			}
			await this.calculateTankVolume();
		} else{
			this.formulaInputs = [];
			this.formulaParameters = {};
		}
	}

	public async provisionTank(): Promise<void> {
		this.submitted = true;
		if (
			this.name === null ||
			this.name.trim().length === 0 ||
			this.feedTypeId == null ||
			(this.nominalValid() === false && this.isRadarSelected && this.isVolume()) ||
			(this.nominalWeightValid() === false ) ||
			(this.isRadarSelected && this.numTanksValid === false) ||
			(this.isStrainSelected && this.numLegsValid === false)
		) {
			return;
		}

		if (this.isRadarSelected) {
			let hasEmpty = false;
			this.formulaInputs.forEach((input: FormulaInput) => {
				input.valid = input.value != null;
				if (input.value == null) hasEmpty = true;
			});
			if (hasEmpty) return;
		}

		this.feedTank.name = this.name;
		this.feedTank.nominalVolume = this.nominalVolume ?? undefined;
		/* convert nominalWeight from tonnes to kg as the API expects kg */
		this.feedTank.nominalWeight = this.nominalWeight * 1000 ?? undefined;
		this.feedTank.numTanks = this.numTanks;
		this.feedTank.feedtype = <FeedType>this.rawFeedType() ?? null;
		this.feedTank.feedTypeId = this.feedTypeId ?? undefined;
		this.feedTank.type = this.feedTankType;
		this.feedTank.sensorType = this.sensorType;
		this.feedTank.numLegs = this.isStrainSelected ? this.numLegs || null : null;
		this.feedTank.legShape = this.isStrainSelected ? this.legShape || null : null;
		this.feedTank.brand = this.isStrainSelected ? this.brand || null : null;
		this.feedTank.formulaParameters = {};
		this.formulaInputs.forEach(
			i => ((this.feedTank.formulaParameters as Record<string, any>)[i.name] = i.value),
		);

		await this.onProvision(this.feedTank);
	}

	public dismiss(): void {
		this.$bvModal.hide(this.modalId);
	}

	public resetValues(): void {
		this.name = this.feedTank.name ?? null;
		this.nominalVolume = this.feedTank.nominalVolume ?? null;
		// Convert kg from API back to tons for display (1 ton = 1000 kg)
		this.nominalWeight = this.feedTank.nominalWeight ? this.feedTank.nominalWeight / 1000 : null;
		this.numTanks = this.feedTank.numTanks ?? 1;
		this.feedTypeId = this.feedTank.feedTypeId ?? null;
		this.sensorType = this.feedTank.sensorType ?? 'radar';
		this.numLegs = this.feedTank.numLegs ?? null;
		this.legShape = this.feedTank.legShape ?? null;
		this.brand = this.feedTank.brand ?? null;
	}

	private async getFeedTypes(): Promise<FeedType[] | undefined> {
		const feedTypesResponse = await new FeedProductService().getFeedTypes();
		if (feedTypesResponse.errorMessage) {
			this.toast.makeToast(feedTypesResponse.errorMessage!, 'Error', 'danger');
			return;
		}
		return feedTypesResponse.feedTypes ?? [];
	}

	private onFormulaChanged(formulaId: number): void {
		this.feedTank.formulaId = formulaId;
		this.formulaInputs = this.formulasService.getFormulaInputs(this.feedTank.formulaId) ?? [];
		this.calculateTankVolume();
	}

	private onFormulaParametersChanged(inputs: FormulaInput[]): void {
		this.formulaInputs = inputs;
		this.calculateTankVolume();
	}

	private async calculateTankVolume() {
		if (!this.feedTank?.id) {
			this.toast.makeToast('No feed tank ID', 'Error Calculating Volume', 'danger');
			return;
		}

		this.calculatingTank = true;
		this.formulaInputs.forEach(i => (this.parameters[i.name] = i.value));
		try {
			if (this.feedTank.formulaId) {
				const formula = this.formulasService.formulas?.find(f => f.id === this.feedTank.formulaId);
				if (!formula) {
					this.calculatingTank = false;
					return;
				}
				this.calculation = await FuelTankVolumeCalculator.calculate(
					9999999, // mod
					formula.formula ?? '',
					formula.name,
					this.feedTank.id,
					this.parameters,
					{ width: 0, height: 0, length: 0, offset: 0 },
					9999999,
					9999999,
					9999999,
					9999999,
				);
			}
		} catch (e) {
			console.log(e);
		}
		this.calculatingTank = false;
	}

	private calculatedNominalDifferent(): boolean {
		return (
			Math.abs((this.nominalVolume ?? 0) - this.calculation.maxVolume) >
			this.calculation.maxVolume * 0.1
		);
	}

	private calculatedNominalWeightDifferent(): boolean {
		const maxVolumeInTonnes = this.calculation.maxVolume * this.rawFeedTypeDensity();
		return Math.abs((this.nominalWeight ?? 0) - maxVolumeInTonnes) > maxVolumeInTonnes * 0.1;
	}

	public isVolume(): boolean {
		return this.rawFeedType()?.levelMetric === FeedTypeLevelMetricEnum.Volume;
	}

	public rawFeedTypeDensity(): number {
		return this.rawFeedType()?.density ?? 1;
	}

	public rawFeedType(): FeedType | null {
		return this.rawFeedTypes?.find(x => x.id === this.feedTypeId) ?? null;
	}
}
